import React, { useState, useEffect, useRef } from 'react';
import { fetchAllGames, createGame, updateGame, deleteGame } from '../../services/apiService';
import BASE_URL from '../../services/baseUrl';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

const SERVER_URL = BASE_URL.replace(/\/api\/?$/, '');

const CATEGORIES = ['slots', 'sweepstakes', 'fishing', 'arcade', 'table', 'other'];
const PER_PAGE = 10;

const defaultForm = {
    name: '',
    description: '',
    category: 'slots',
    minDeposit: '',
    maxDeposit: '',
    isFeatured: false,
    order: '',
    thumbnail: null,
};

const categoryBadgeClass = (cat) => {
    const map = {
        slots: 'bg-warning text-dark',
        sweepstakes: 'bg-success text-white',
        fishing: 'bg-primary text-white',
        arcade: 'bg-danger text-white',
        table: 'bg-info text-dark',
        other: 'bg-secondary text-white',
    };
    return map[cat] || 'bg-secondary text-white';
};

const GamesManagement = () => {
    const token = localStorage.getItem('accessToken');

    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });

    // Modal mode: 'add' | 'edit'
    const [modalMode, setModalMode] = useState('add');
    const [showModal, setShowModal] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);

    const [form, setForm] = useState(defaultForm);
    const [submitting, setSubmitting] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    const fileRef = useRef();

    // ── Load games ──────────────────────────────────────────────────────────
    const loadGames = async (page = 1) => {
        setLoading(true);
        try {
            const res = await fetchAllGames(token, page, PER_PAGE);
            setGames(res.data.games);
            setPagination(res.data.pagination);
        } catch {
            toast.error('Failed to load games');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadGames(); }, []);

    // ── Form helpers ─────────────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setForm(f => ({ ...f, thumbnail: file }));
        setPreviewUrl(URL.createObjectURL(file));
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    };

    // ── FIX: never send thumbnail unless it's a real File object ────────────
    const buildFormData = (formObj) => {
        const fd = new FormData();
        Object.entries(formObj).forEach(([k, v]) => {
            if (k === 'thumbnail') {
                // Only append if user actually selected a new file
                if (v instanceof File) fd.append(k, v);
                // Skip entirely if null, string URL, or anything else
                return;
            }
            if (v !== null && v !== '' && v !== undefined) {
                fd.append(k, v);
            }
        });
        return fd;
    };

    // ── Open modals ──────────────────────────────────────────────────────────
    const openAddModal = () => {
        setModalMode('add');
        setSelectedGame(null);
        setForm(defaultForm);
        setPreviewUrl(null);
        setShowModal(true);
    };

    const openEditModal = (game) => {
        setModalMode('edit');
        setSelectedGame(game);
        setForm({
            name: game.name || '',
            description: game.description || '',
            category: game.category || 'slots',
            minDeposit: game.minDeposit ?? '',
            maxDeposit: game.maxDeposit ?? '',
            isFeatured: game.isFeatured || false,
            order: game.order ?? '',
            thumbnail: null, // keep null; user uploads new one only if they want
        });
        setPreviewUrl(game.thumbnail ? `${SERVER_URL}${game.thumbnail}` : null);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedGame(null);
        setForm(defaultForm);
        setPreviewUrl(null);
        if (fileRef.current) fileRef.current.value = '';
    };

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteGame(token, deleteTarget._id);
            toast.success(`"${deleteTarget.name}" deleted successfully`);
            setDeleteTarget(null);
            const newPage = games.length === 1 && pagination.page > 1
                ? pagination.page - 1
                : pagination.page;
            loadGames(newPage);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to delete game');
        } finally {
            setDeleting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const fd = buildFormData(form);

            if (modalMode === 'add') {
                await createGame(token, fd);
                toast.success('Game created successfully!');
            } else {
                await updateGame(token, selectedGame._id, fd);
                toast.success('Game updated successfully!');
            }

            closeModal();
            loadGames(pagination.page);
        } catch (err) {
            toast.error(err.response?.data?.message || `Failed to ${modalMode === 'add' ? 'create' : 'update'} game`);
        } finally {
            setSubmitting(false);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="container-fluid py-4 px-3 px-md-4">
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <div>
                    <h2 className="fw-bold mb-0">Games Management</h2>
                    <p className="text-muted small mb-0">{pagination.total} games in your platform</p>
                </div>
                <button className="btn btn-primary" onClick={openAddModal}>
                    + Add New Game
                </button>
            </div>

            {/* Stats Cards */}
            <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Total Games</p>
                            <h3 className="fw-bold text-primary mb-0">{pagination.total}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Featured</p>
                            <h3 className="fw-bold text-warning mb-0">{games.filter(g => g.isFeatured).length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Active</p>
                            <h3 className="fw-bold text-success mb-0">{games.filter(g => g.isActive).length}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-6 col-md-3">
                    <div className="card border-0 shadow-sm h-100">
                        <div className="card-body">
                            <p className="text-muted small mb-1 text-uppercase fw-semibold">Pages</p>
                            <h3 className="fw-bold text-info mb-0">{pagination.pages}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center py-5">
                            <div className="spinner-border text-primary me-2" role="status"></div>
                            <span className="text-muted">Loading games...</span>
                        </div>
                    ) : games.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="fs-1 mb-2">🎮</div>
                            <p className="text-muted">No games found. Add your first game!</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="ps-3">#</th>
                                        <th>Game</th>
                                        <th className="d-none d-sm-table-cell">Category</th>
                                        <th className="d-none d-md-table-cell">Deposit Range</th>
                                        <th className="d-none d-md-table-cell">Rooms</th>
                                        <th className="d-none d-sm-table-cell">Featured</th>
                                        <th>Status</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {games.map((game, i) => (
                                        <tr key={game._id}>
                                            <td className="ps-3 text-muted small">
                                                {(pagination.page - 1) * PER_PAGE + i + 1}
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    {game.thumbnail ? (
                                                        <img
                                                            src={`${SERVER_URL}${game.thumbnail}`}
                                                            alt={game.name}
                                                            className="rounded"
                                                            style={{ width: 48, height: 48, objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="rounded d-flex align-items-center justify-content-center text-muted"
                                                            style={{ backgroundColor: '#122418' }}
                                                            style={{ width: 48, height: 48, fontSize: '1.3rem' }}
                                                        >🎮</div>
                                                    )}
                                                    <div>
                                                        <div className="fw-semibold">{game.name}</div>
                                                        <div className="text-muted small">{game.slug}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="d-none d-sm-table-cell">
                                                <span className={`badge rounded-pill ${categoryBadgeClass(game.category)}`}>
                                                    {game.category}
                                                </span>
                                            </td>
                                            <td className="d-none d-md-table-cell text-muted small">
                                                ${game.minDeposit} — ${game.maxDeposit}
                                            </td>
                                            <td className="d-none d-md-table-cell text-muted">
                                                {game.totalRooms}
                                            </td>
                                            <td className="d-none d-sm-table-cell">
                                                {game.isFeatured
                                                    ? <span className="text-warning fw-semibold small">⭐ Yes</span>
                                                    : <span className="text-muted small">— No</span>
                                                }
                                            </td>
                                            <td>
                                                <span className={`badge ${game.isActive ? 'bg-success' : 'bg-danger'}`}>
                                                    {game.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className='d-flex gap-2 py-4'>
                                                <button
                                                    className="btn btn-sm btn-outline-primary"
                                                    onClick={() => openEditModal(game)}
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setDeleteTarget(game)}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Pagination */}
                    {!loading && games.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 px-3 py-3 border-top">
                            <span className="text-muted small">
                                Page {pagination.page} of {pagination.pages} · {pagination.total} total
                            </span>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => loadGames(pagination.page - 1)}>
                                            &laquo; Prev
                                        </button>
                                    </li>
                                    {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                                        .filter(p => Math.abs(p - pagination.page) <= 2)
                                        .map(p => (
                                            <li key={p} className={`page-item ${p === pagination.page ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => loadGames(p)}>{p}</button>
                                            </li>
                                        ))
                                    }
                                    <li className={`page-item ${pagination.page >= pagination.pages ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => loadGames(pagination.page + 1)}>
                                            Next &raquo;
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            {/* Add / Edit Modal */}
            {showModal && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title fw-bold">
                                        {modalMode === 'add' ? 'Add New Game' : `Edit Game — ${selectedGame?.name}`}
                                    </h5>
                                    <button type="button" className="btn-close" onClick={closeModal}></button>
                                </div>
                                <div className="modal-body">
                                    <form onSubmit={handleSubmit} id="gameForm">
                                        <div className="row g-3">

                                            {/* Name */}
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">
                                                    Game Name <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="name"
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    placeholder="e.g. New Fish Game"
                                                    required
                                                />
                                            </div>

                                            {/* Description */}
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">Description</label>
                                                <textarea
                                                    className="form-control"
                                                    name="description"
                                                    value={form.description}
                                                    onChange={handleChange}
                                                    rows={3}
                                                    placeholder="Brief description of the game..."
                                                />
                                            </div>

                                            {/* Category & Order */}
                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">
                                                    Category <span className="text-danger">*</span>
                                                </label>
                                                <select
                                                    className="form-select"
                                                    name="category"
                                                    value={form.category}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    {CATEGORIES.map(c => (
                                                        <option key={c} value={c}>
                                                            {c.charAt(0).toUpperCase() + c.slice(1)}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">Display Order</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="order"
                                                    value={form.order}
                                                    onChange={handleChange}
                                                    placeholder="e.g. 15"
                                                    min="1"
                                                />
                                            </div>

                                            {/* Min & Max Deposit */}
                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">
                                                    Min Deposit ($) <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="minDeposit"
                                                    value={form.minDeposit}
                                                    onChange={handleChange}
                                                    placeholder="5"
                                                    required
                                                    min="0"
                                                />
                                            </div>

                                            <div className="col-sm-6">
                                                <label className="form-label fw-semibold">
                                                    Max Deposit ($) <span className="text-danger">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="maxDeposit"
                                                    value={form.maxDeposit}
                                                    onChange={handleChange}
                                                    placeholder="5000"
                                                    required
                                                    min="0"
                                                />
                                            </div>

                                            {/* Featured Toggle */}
                                            <div className="col-12">
                                                <div className="form-check form-switch">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        role="switch"
                                                        id="isFeatured"
                                                        name="isFeatured"
                                                        checked={form.isFeatured}
                                                        onChange={handleChange}
                                                    />
                                                    <label className="form-check-label fw-semibold" htmlFor="isFeatured">
                                                        Mark as Featured
                                                    </label>
                                                </div>
                                            </div>

                                            {/* Thumbnail Upload */}
                                            <div className="col-12">
                                                <label className="form-label fw-semibold">
                                                    Thumbnail Image
                                                    {modalMode === 'edit' && (
                                                        <span className="text-muted fw-normal small ms-1">
                                                            (leave empty to keep current)
                                                        </span>
                                                    )}
                                                </label>
                                                <input
                                                    ref={fileRef}
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    onChange={handleFileChange}
                                                />
                                                <div className="form-text">JPG, PNG, WEBP · Max 5MB</div>
                                                {previewUrl && (
                                                    <div className="mt-2">
                                                        <p className="small text-muted mb-1">
                                                            {modalMode === 'edit' && !(form.thumbnail instanceof File)
                                                                ? 'Current thumbnail:'
                                                                : 'Preview:'}
                                                        </p>
                                                        <img
                                                            src={previewUrl}
                                                            alt="Preview"
                                                            className="img-thumbnail d-block"
                                                            style={{ maxHeight: 150, objectFit: 'cover' }}
                                                        />
                                                    </div>
                                                )}
                                            </div>

                                        </div>
                                    </form>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        form="gameForm"
                                        className="btn btn-primary"
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                {modalMode === 'add' ? 'Creating...' : 'Updating...'}
                                            </>
                                        ) : (
                                            modalMode === 'add' ? 'Create Game' : 'Save Changes'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}

            {/* Delete Confirm Modal */}
            {deleteTarget && (
                <>
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header border-0 pb-0">
                                    <h5 className="modal-title fw-bold text-danger">Delete Game</h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setDeleteTarget(null)}
                                        disabled={deleting}
                                    ></button>
                                </div>
                                <div className="modal-body pt-2">
                                    <div className="d-flex align-items-center gap-3 mb-3">
                                        {deleteTarget.thumbnail ? (
                                            <img
                                                src={`${SERVER_URL}${deleteTarget.thumbnail}`}
                                                alt={deleteTarget.name}
                                                className="rounded"
                                                style={{ width: 56, height: 56, objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div
                                                className="rounded bg-light d-flex align-items-center justify-content-center text-muted"
                                                style={{ width: 56, height: 56, fontSize: '1.5rem' }}
                                            >🎮</div>
                                        )}
                                        <div>
                                            <div className="fw-semibold">{deleteTarget.name}</div>
                                            <div className="text-muted small">{deleteTarget.slug}</div>
                                        </div>
                                    </div>
                                    <p className="text-muted mb-0">
                                        Are you sure you want to delete this game? This action
                                        <strong> cannot be undone</strong>.
                                    </p>
                                </div>
                                <div className="modal-footer border-0 pt-0">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={() => setDeleteTarget(null)}
                                        disabled={deleting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleDeleteConfirm}
                                        disabled={deleting}
                                    >
                                        {deleting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Deleting...
                                            </>
                                        ) : (
                                            'Yes, Delete'
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop fade show"></div>
                </>
            )}
        </div>
    );
};

export default GamesManagement;